/**
 * V1 Lean Content Engine — Admin Weekly Email Package page.
 *
 * Buyer's Leverage ships articles daily but emails weekly. This page is
 * the admin workflow surface for the random-eligible-article weekly
 * email package generator:
 *
 *   1. Click "Generate weekly email" → backend picks an eligible
 *      article (or admin forces a specific one) and produces a draft.
 *   2. Review the generated copy + infographic.
 *   3. Approve → Schedule → Mark sent.
 *
 * Article eligibility is reset by the clean-slate legacy migration so
 * the first V1 weekly emails treat every published article as fresh.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, Mail, Send, CheckCircle, Archive, Eye,
    AlertCircle, Info, RotateCcw, Calendar, X, Users, AlertTriangle,
} from "lucide-react";
import {
    adminBroadcastWeeklyEmail,
    adminGenerateWeeklyEmail,
    adminGetWeeklyEmailAudienceCount,
    adminListWeeklyEmails,
    adminListWeeklyEmailCandidates,
    adminPreviewWeeklyEmail,
    adminResetArticleEmailStatus,
    adminSendWeeklyEmailTest,
    adminTransitionWeeklyEmail,
} from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
    draft_created: "bg-bl-bg text-bl border-bl",
    approved: "bg-[var(--bl-teal-tint)] text-bl-primary border-bl-primary",
    scheduled: "bg-[var(--bl-amber-soft)] text-[var(--bl-amber)] border-[var(--bl-amber)]",
    sent: "bg-[var(--bl-green-soft)] text-[var(--bl-green)] border-[var(--bl-green)]",
    archived: "bg-bl-bg text-bl-muted border-bl",
};


function CandidatePool({ candidates, onForceGenerate, busy }) {
    if (!candidates.length) {
        return (
            <div
                className="rounded border border-bl-line p-4 text-xs text-bl-muted text-center"
                data-testid="weekly-email-no-candidates"
            >
                No eligible unpublished email articles are available.
                Publish more articles or reset an article&apos;s email status.
            </div>
        );
    }
    return (
        <div className="space-y-1.5" data-testid="weekly-email-candidates">
            {candidates.map((c) => (
                <div
                    key={c.slug}
                    className="flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-bl-bg border border-transparent transition-colors"
                    data-testid={`candidate-${c.slug}`}
                >
                    <span className="flex-1 truncate text-bl font-medium">{c.title}</span>
                    <span className="text-bl-muted hidden sm:inline">
                        {c.category}
                    </span>
                    {!c.rotation_eligible && (
                        <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bl-amber-soft)] text-[var(--bl-amber)]"
                            title="This category was used in one of the last 5 emails"
                        >
                            recent cat
                        </span>
                    )}
                    <button
                        type="button"
                        className="bl-btn-ghost text-[10px] px-2 py-0.5"
                        onClick={() => onForceGenerate(c.slug)}
                        disabled={busy}
                        data-testid={`force-generate-${c.slug}`}
                    >
                        Use this
                    </button>
                </div>
            ))}
        </div>
    );
}


function EmailPackageRow({ pkg, onTransition, onReset, onPreview, busyId, busyAction }) {
    const badge = STATUS_BADGE[pkg.status] || STATUS_BADGE.draft_created;
    const isBusy = busyId === pkg.email_package_id;
    return (
        <div
            className="bl-card p-4 space-y-3"
            data-testid={`email-pkg-${pkg.email_package_id}`}
        >
            <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                    <div className="font-display font-semibold text-bl text-sm mb-0.5">
                        {pkg.article_title}
                    </div>
                    <div className="text-xs text-bl-muted flex flex-wrap items-center gap-2">
                        <span>{pkg.article_category}</span>
                        <span>·</span>
                        <Link
                            to={`/learn/${pkg.article_slug}`}
                            className="text-bl-primary hover:underline"
                            target="_blank"
                            rel="noreferrer"
                        >
                            /learn/{pkg.article_slug}
                        </Link>
                        {pkg.related_calculator_slug && (
                            <>
                                <span>·</span>
                                <Link
                                    to={`/tools/${pkg.related_calculator_slug}`}
                                    className="text-bl-primary hover:underline"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    /tools/{pkg.related_calculator_slug}
                                </Link>
                            </>
                        )}
                    </div>
                </div>
                <span
                    className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase tracking-wide ${badge}`}
                    data-testid={`email-pkg-status-${pkg.email_package_id}`}
                >
                    {pkg.status}
                </span>
            </div>

            {pkg.infographic_missing && (
                <div className="text-[11px] text-[var(--bl-amber)] flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Article has no infographic — email will fall back to hero image.
                </div>
            )}

            {pkg.copy?.subject_line_options && (
                <div className="text-xs">
                    <div className="text-bl-muted mb-1 font-medium">
                        Subject line options:
                    </div>
                    <ul className="space-y-0.5 list-disc list-inside text-bl">
                        {(pkg.copy.subject_line_options || []).slice(0, 3).map((s, i) => (
                            <li key={i} className="truncate">{s}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-bl-line">
                <button
                    type="button"
                    onClick={() => onPreview(pkg)}
                    className="bl-btn-ghost text-xs gap-1 border border-bl-primary text-bl-primary"
                    data-testid={`email-pkg-preview-${pkg.email_package_id}`}
                >
                    <Eye className="h-3 w-3" />
                    Preview email
                </button>
                {pkg.status === "draft_created" && (
                    <button
                        type="button"
                        onClick={() => onTransition(pkg.email_package_id, "approve")}
                        disabled={isBusy}
                        className="bl-btn-ghost text-xs gap-1 border border-bl-primary text-bl-primary"
                        data-testid={`email-pkg-approve-${pkg.email_package_id}`}
                    >
                        {isBusy && busyAction === "approve" ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <CheckCircle className="h-3 w-3" />
                        )}
                        Approve
                    </button>
                )}
                {(pkg.status === "approved" || pkg.status === "draft_created") && (
                    <button
                        type="button"
                        onClick={() => onTransition(pkg.email_package_id, "schedule")}
                        disabled={isBusy}
                        className="bl-btn-ghost text-xs gap-1 border border-bl-primary text-bl-primary"
                        data-testid={`email-pkg-schedule-${pkg.email_package_id}`}
                    >
                        {isBusy && busyAction === "schedule" ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Calendar className="h-3 w-3" />
                        )}
                        Schedule
                    </button>
                )}
                {pkg.status === "scheduled" && (
                    <button
                        type="button"
                        onClick={() => onTransition(pkg.email_package_id, "mark_sent")}
                        disabled={isBusy}
                        className="bl-btn-primary text-xs gap-1"
                        data-testid={`email-pkg-mark-sent-${pkg.email_package_id}`}
                    >
                        {isBusy && busyAction === "mark_sent" ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Send className="h-3 w-3" />
                        )}
                        Mark sent
                    </button>
                )}
                {pkg.status !== "archived" && (
                    <button
                        type="button"
                        onClick={() => onTransition(pkg.email_package_id, "archive")}
                        disabled={isBusy}
                        className="bl-btn-ghost text-xs gap-1 text-bl-muted"
                        data-testid={`email-pkg-archive-${pkg.email_package_id}`}
                    >
                        <Archive className="h-3 w-3" />
                        Archive
                    </button>
                )}
                {(pkg.status === "sent" || pkg.status === "scheduled") && (
                    <button
                        type="button"
                        onClick={() => onReset(pkg.article_slug)}
                        disabled={isBusy}
                        className="bl-btn-ghost text-xs gap-1 text-bl-muted ml-auto"
                        data-testid={`email-pkg-reset-${pkg.email_package_id}`}
                        title="Make this article eligible for future weekly emails again"
                    >
                        <RotateCcw className="h-3 w-3" />
                        Reset article eligibility
                    </button>
                )}
            </div>
        </div>
    );
}


function EmailPreviewModal({ pkg, onClose, onBroadcastDone }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testEmail, setTestEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [audience, setAudience] = useState(null); // {subscriber_count}
    const [confirmBroadcast, setConfirmBroadcast] = useState(false);
    const [broadcasting, setBroadcasting] = useState(false);
    const [broadcastResult, setBroadcastResult] = useState(null);

    useEffect(() => {
        if (!pkg) return undefined;
        let alive = true;
        (async () => {
            try {
                const [r, a] = await Promise.all([
                    adminPreviewWeeklyEmail(pkg.email_package_id),
                    adminGetWeeklyEmailAudienceCount(pkg.email_package_id)
                        .catch(() => null),
                ]);
                if (!alive) return;
                setData(r);
                setAudience(a);
                setError(null);
            } catch (e) {
                if (alive) setError(e?.response?.data?.detail || e.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [pkg]);

    if (!pkg) return null;

    const alreadySent = pkg.status === "sent";

    const handleSendTest = async () => {
        setSending(true);
        try {
            const r = await adminSendWeeklyEmailTest(
                pkg.email_package_id,
                testEmail || undefined,
            );
            if (r.sent) {
                toast.success(`Test email sent to ${r.to}.`);
            } else {
                toast.message(
                    `Pipeline didn't send (sandbox / throttled / no Resend key).`,
                    { description: `Recipient: ${r.to}` },
                );
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test send failed.");
        } finally {
            setSending(false);
        }
    };

    const handleBroadcast = async () => {
        setBroadcasting(true);
        try {
            const r = await adminBroadcastWeeklyEmail(pkg.email_package_id);
            setBroadcastResult(r);
            toast.success(
                `Sent to ${r.sent} of ${r.subscribers_checked} subscribers`
                + (r.suppressed ? ` · ${r.suppressed} suppressed` : "")
                + (r.errors ? ` · ${r.errors} errors` : ""),
            );
            if (onBroadcastDone) onBroadcastDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Broadcast failed.");
        } finally {
            setBroadcasting(false);
            setConfirmBroadcast(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 p-0 sm:p-6"
            data-testid="email-preview-modal"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="bg-bl-surface w-full max-w-4xl rounded-none sm:rounded-xl shadow-2xl flex flex-col max-h-screen sm:max-h-[92vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-bl-line">
                    <div className="flex items-center gap-2 min-w-0">
                        <Mail className="h-4 w-4 text-bl-primary shrink-0" />
                        <div className="min-w-0">
                            <div
                                className="text-sm font-display font-semibold text-bl truncate"
                                data-testid="email-preview-subject"
                            >
                                {data?.subject || pkg.article_title}
                            </div>
                            <div className="text-[11px] text-bl-muted truncate">
                                {pkg.article_slug} · {pkg.status}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="bl-btn-ghost text-xs gap-1 px-2"
                        data-testid="email-preview-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto bg-[#F7F5F1]" data-testid="email-preview-iframe-wrap">
                    {loading && (
                        <div className="p-10 text-center text-bl-muted text-sm">
                            <Loader2 className="h-5 w-5 animate-spin inline" />
                            {" "}Rendering preview…
                        </div>
                    )}
                    {error && (
                        <div className="p-6 text-sm text-[var(--bl-rust)]" data-testid="email-preview-error">
                            <AlertCircle className="h-4 w-4 inline mr-1" />
                            {error}
                        </div>
                    )}
                    {data?.html && (
                        <iframe
                            srcDoc={data.html}
                            title="Weekly email preview"
                            className="w-full min-h-[60vh] border-0 bg-white"
                            sandbox=""
                            data-testid="email-preview-iframe"
                        />
                    )}
                </div>

                <div className="px-5 py-3 border-t border-bl-line bg-bl-bg flex items-center gap-2 flex-wrap">
                    {data?.infographic_missing && (
                        <span className="text-[11px] text-[var(--bl-amber)] flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            No claymation infographic on this article.
                        </span>
                    )}
                    <div className="flex items-center gap-2 ml-auto flex-wrap">
                        <input
                            type="email"
                            placeholder="leave blank to send to yourself"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            className="px-3 py-1.5 text-xs border border-bl-line rounded bg-bl-surface text-bl w-64"
                            data-testid="email-preview-test-input"
                        />
                        <button
                            type="button"
                            onClick={handleSendTest}
                            disabled={sending || loading || !data?.html}
                            className="bl-btn-ghost text-xs gap-1.5 border border-bl-primary text-bl-primary"
                            data-testid="email-preview-send-test"
                        >
                            {sending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Send className="h-3 w-3" />
                            )}
                            Send test
                        </button>
                    </div>
                </div>

                {/* Broadcast row — always visible so admins can ship the
                    email to the full subscriber list without leaving the
                    preview. Foot-gun guarded by a two-step confirmation. */}
                <div className="px-5 py-3 border-t border-bl-line bg-bl-surface flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-bl-muted">
                        <Users className="h-4 w-4 text-bl-primary" />
                        <span data-testid="email-preview-audience-count">
                            {audience
                                ? `${audience.subscriber_count} opted-in subscriber${audience.subscriber_count === 1 ? "" : "s"}`
                                : "Loading audience…"}
                        </span>
                    </div>
                    {broadcastResult && (
                        <div
                            className="text-xs text-[var(--bl-green)] flex items-center gap-1.5"
                            data-testid="email-preview-broadcast-result"
                        >
                            <CheckCircle className="h-3 w-3" />
                            Broadcast complete · sent {broadcastResult.sent} of {broadcastResult.subscribers_checked}
                            {broadcastResult.suppressed ? ` · ${broadcastResult.suppressed} suppressed` : ""}
                            {broadcastResult.errors ? ` · ${broadcastResult.errors} errors` : ""}
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        {!confirmBroadcast && !broadcastResult && (
                            <button
                                type="button"
                                onClick={() => setConfirmBroadcast(true)}
                                disabled={
                                    loading || !data?.html
                                    || alreadySent
                                    || !audience || audience.subscriber_count === 0
                                }
                                className="bl-btn-primary text-xs gap-1.5"
                                data-testid="email-preview-broadcast-btn"
                                title={
                                    alreadySent
                                        ? "Already broadcast — reset the package to send again."
                                        : (audience?.subscriber_count === 0
                                            ? "No opted-in subscribers yet."
                                            : "Send to every opted-in subscriber")
                                }
                            >
                                <Send className="h-3 w-3" />
                                {alreadySent ? "Already sent" : "Send to all subscribers"}
                            </button>
                        )}
                        {confirmBroadcast && !broadcastResult && (
                            <div
                                className="flex items-center gap-2 text-xs"
                                data-testid="email-preview-broadcast-confirm"
                            >
                                <AlertTriangle className="h-4 w-4 text-[var(--bl-amber)]" />
                                <span className="text-bl">
                                    Send to {audience?.subscriber_count} subscribers? This can&apos;t be undone.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setConfirmBroadcast(false)}
                                    disabled={broadcasting}
                                    className="bl-btn-ghost text-xs px-2 py-1"
                                    data-testid="email-preview-broadcast-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBroadcast}
                                    disabled={broadcasting}
                                    className="bl-btn-primary text-xs gap-1.5"
                                    data-testid="email-preview-broadcast-confirm-btn"
                                >
                                    {broadcasting ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <Send className="h-3 w-3" />
                                    )}
                                    Yes, send now
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function AdminWeeklyEmails() {
    const [list, setList] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [recentCats, setRecentCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [busyAction, setBusyAction] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [showCandidates, setShowCandidates] = useState(false);
    const [previewPkg, setPreviewPkg] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const [emails, cands] = await Promise.all([
                    adminListWeeklyEmails({ limit: 50 }),
                    adminListWeeklyEmailCandidates(50),
                ]);
                if (!alive) return;
                setList(emails.items || []);
                setCandidates(cands.candidates || []);
                setRecentCats(cands.recent_categories || []);
            } catch {
                toast.error("Could not load weekly emails.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [refreshTick]);

    const handleGenerate = async (forcedSlug = null) => {
        setGenerating(true);
        try {
            const r = await adminGenerateWeeklyEmail(forcedSlug);
            toast.success(
                forcedSlug
                    ? `Generated email for "${forcedSlug}"`
                    : `Generated weekly email for "${r.package.article_slug}"`,
            );
            setRefreshTick((t) => t + 1);
            setShowCandidates(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed.");
        } finally {
            setGenerating(false);
        }
    };

    const handleTransition = async (pkgId, action) => {
        setBusyId(pkgId);
        setBusyAction(action);
        try {
            await adminTransitionWeeklyEmail(pkgId, action);
            toast.success(`Email package → ${action}`);
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Transition failed.");
        } finally {
            setBusyId(null);
            setBusyAction(null);
        }
    };

    const handleResetArticle = async (slug) => {
        try {
            await adminResetArticleEmailStatus(slug);
            toast.success(`Article "${slug}" eligibility reset to not_used.`);
            setRefreshTick((t) => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed.");
        }
    };

    return (
        <div
            className="container max-w-5xl mx-auto px-4 py-8 space-y-6"
            data-testid="admin-weekly-emails-page"
        >
            <div className="flex items-start gap-3 justify-between flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-semibold text-bl flex items-center gap-2">
                        <Mail className="h-6 w-6" />
                        Weekly Email Packages
                    </h1>
                    <p className="text-xs text-bl-muted mt-1 max-w-xl">
                        V1 Lean Content Engine — Buyer&apos;s Leverage publishes articles daily
                        but emails weekly. Click Generate to randomly pick one eligible
                        published article, reuse its infographic, and produce a draft.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setShowCandidates((s) => !s)}
                        className="bl-btn-ghost text-xs gap-1 border border-bl"
                        data-testid="weekly-toggle-candidates"
                    >
                        <Eye className="h-3 w-3" />
                        {showCandidates ? "Hide" : "View"} eligible pool ({candidates.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => handleGenerate(null)}
                        disabled={generating || candidates.length === 0}
                        className="bl-btn-primary text-xs gap-1.5"
                        data-testid="weekly-generate-btn"
                    >
                        {generating ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Mail className="h-3 w-3" />
                        )}
                        Generate weekly email
                    </button>
                </div>
            </div>

            {recentCats.length > 0 && (
                <div
                    className="text-[11px] text-bl-muted flex items-center gap-1.5"
                    data-testid="weekly-recent-categories"
                >
                    <Info className="h-3 w-3" />
                    Recent email categories (avoided by rotation):{" "}
                    <span className="font-mono">
                        {recentCats.filter(Boolean).join(" · ") || "—"}
                    </span>
                </div>
            )}

            {showCandidates && (
                <div className="bl-card p-4 space-y-2">
                    <div className="text-xs font-medium text-bl mb-1">
                        Eligible article pool ({candidates.length})
                    </div>
                    <CandidatePool
                        candidates={candidates}
                        onForceGenerate={handleGenerate}
                        busy={generating}
                    />
                </div>
            )}

            <div className="space-y-3" data-testid="weekly-email-list">
                <div className="text-xs text-bl-muted">
                    {list.length} email package{list.length === 1 ? "" : "s"}
                </div>
                {loading ? (
                    <div className="text-center py-8 text-bl-muted text-sm">
                        <Loader2 className="h-4 w-4 animate-spin inline" />
                        {" "}Loading…
                    </div>
                ) : list.length === 0 ? (
                    <div
                        className="bl-card p-8 text-center text-sm text-bl-muted"
                        data-testid="weekly-email-empty-state"
                    >
                        <Mail className="h-8 w-8 text-bl-muted mx-auto mb-2" />
                        No weekly email packages yet. Click{" "}
                        <strong>Generate weekly email</strong> above to ship the first one.
                    </div>
                ) : (
                    list.map((pkg) => (
                        <EmailPackageRow
                            key={pkg.email_package_id}
                            pkg={pkg}
                            onTransition={handleTransition}
                            onReset={handleResetArticle}
                            onPreview={setPreviewPkg}
                            busyId={busyId}
                            busyAction={busyAction}
                        />
                    ))
                )}
            </div>

            {previewPkg && (
                <EmailPreviewModal
                    pkg={previewPkg}
                    onClose={() => setPreviewPkg(null)}
                    onBroadcastDone={() => setRefreshTick((t) => t + 1)}
                />
            )}
        </div>
    );
}
